import React, { FC, useEffect, useState } from "react";
import tzMoment from "moment-timezone";
import { Card, Button, Select, notification } from "antd";
import { ASAP, getTodaysHours } from "../helpers";
import {
  Location,
  TimeSlotAvailability,
} from "../queries/auto-generated/schemas";
import styled from "styled-components";
import withMoreInfo from "./location/withMoreInfo";
import { OnResetTimeFunc, OnSetTimeFunc, TodaysHours } from "../types";
import { createHumanReadable } from "../util/time-management";
import {
  HUMAN_OUTPUT_FORMAT,
  LOCATION_AVAILABILITY_POLL_INTERVAL,
  MISSED_LEAD_TIME_MESSAGE,
  REQUEST_TIME_NO_LONGER_AVAILABLE_MESSAGE,
} from "../constants";
import { useLocationAvailabilityQuery } from "../queries/auto-generated/hooks";

const { Option } = Select;

const TimeScheduler: FC<{
  shopLocation: Location;
  onSetTime: OnSetTimeFunc;
  requestedPickupTime?: string;
  onResetTime?: OnResetTimeFunc;
  showCancelButton?: boolean;
  showEditTop?: boolean;
  canEdit?: boolean;
  forOrderPage?: boolean;
}> = ({
  shopLocation,
  onSetTime,
  requestedPickupTime,
  onResetTime,
  showCancelButton,
  showEditTop = false,
  canEdit = true,
  forOrderPage,
}) => {
  const availabilityQuery = useLocationAvailabilityQuery({
    variables: { locationId: shopLocation.id },
    fetchPolicy: "cache-and-network",
    pollInterval: LOCATION_AVAILABILITY_POLL_INTERVAL,
  });

  const [firstSlots, setFirstSlots] = useState<TimeSlotAvailability[]>([]);
  const [allSlots, setAllSlots] = useState<TimeSlotAvailability[]>([]);
  const [requestInFirstSlots, setRequestInFirstSlots] = useState<boolean>(true);
  const [timezone] = useState<string>(shopLocation?.timezone ?? "UTC");
  const [locationHours] = useState<TodaysHours | null>(
    getTodaysHours(shopLocation)
  );
  const [canOrder, setCanOrder] = useState<boolean>(
    !!locationHours && locationHours.open
  );
  const [showEditOptions, setShowEditOptions] = useState<boolean>(
    !requestedPickupTime
  );
  const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);

  // Handle location availability query
  useEffect(() => {
    if (availabilityQuery.data) {
      const allSlots = availabilityQuery.data.locationAvailability;
      const available = allSlots?.filter((entry) => entry.available) || [];
      const firstSlots = available.slice(0, 4);

      const requestInFirstSlots = !!firstSlots.find(
        (item) => item.timeSlot === requestedPickupTime
      );
      setAllSlots(allSlots);
      setFirstSlots(firstSlots);
      setRequestInFirstSlots(requestInFirstSlots);

      // Set time to undefined if the requested pickup time is unavailable
      if (
        !forOrderPage &&
        requestedPickupTime !== undefined &&
        !available.find((item) => item.timeSlot === requestedPickupTime)
      ) {
        onSetTime(undefined);
        notification.open({
          message: REQUEST_TIME_NO_LONGER_AVAILABLE_MESSAGE,
          placement: "bottomRight",
          duration: 0,
        });
      }
    }
  }, [availabilityQuery.data, requestedPickupTime, onSetTime, forOrderPage]);

  // On mount
  // Prevent UI from allowing new orders when the location is closed
  useEffect(() => {
    if (!canOrder) {
      onSetTime(undefined); // Reset pickup time
    }

    const timerId = setInterval(() => {
      const currentLocationHours = getTodaysHours(shopLocation);
      // If there are hours to compare and the open status is different than canOrder
      if (!!currentLocationHours && currentLocationHours.open !== canOrder) {
        setCanOrder(currentLocationHours.open);
        if (!currentLocationHours.open) {
          onSetTime(undefined); // Reset pickup time
        }
      }
    }, 1000); // Update every second

    return () => clearTimeout(timerId); // Clear timer on unmount
    // eslint-disable-next-line
  }, []);

  if (!canOrder && !forOrderPage) {
    const ButtonWithMoreInfo = withMoreInfo(Button);

    return (
      <Card
        title="Closed"
        bodyStyle={{ textAlign: "center" }}
        loading={loadingUpdate || availabilityQuery.loading}
      >
        <Message>The store is now closed.</Message>
        <ButtonWithMoreInfo location={shopLocation} danger>
          View store hours
        </ButtonWithMoreInfo>
      </Card>
    );
  }

  const editButton = canEdit ? (
    <Button
      block={!showEditTop}
      shape="round"
      size={showEditTop ? "middle" : "large"}
      type={"text"}
      onClick={() => {
        onResetTime && onResetTime();
        setShowEditOptions(true);
      }}
    >
      Edit
    </Button>
  ) : null;

  const cancelButton = showCancelButton && (
    <Button
      type="text"
      shape="round"
      block
      onClick={() => setShowEditOptions(false)}
    >
      Back
    </Button>
  );

  const handleChange = (value: string) => {
    setLoadingUpdate(true);
    setShowEditOptions(false);
    onSetTime(value)
      .then(() => {
        if (forOrderPage && availabilityQuery.refetch) {
          availabilityQuery.refetch().finally(() => setLoadingUpdate(false));
        } else {
          setLoadingUpdate(false);
        }
      })
      .catch(() => setLoadingUpdate(false));
  };

  if (allSlots.length === 0 && !forOrderPage) {
    const ButtonWithMoreInfo = withMoreInfo(Button);

    return (
      <Card
        title="Almost closed"
        bodyStyle={{ textAlign: "center" }}
        loading={loadingUpdate || availabilityQuery.loading}
      >
        <Message>{MISSED_LEAD_TIME_MESSAGE}</Message>
        <ButtonWithMoreInfo location={shopLocation} danger>
          View store hours
        </ButtonWithMoreInfo>
      </Card>
    );
  }

  const Content: FC = () => {
    return (
      <>
        <TimeBlocks>
          {firstSlots.map(({ timeSlot }) => {
            const humanLabel = createHumanReadable(timeSlot, timezone);
            return (
              <Button
                key={timeSlot}
                onClick={() => handleChange(timeSlot)}
                type={requestedPickupTime === timeSlot ? "primary" : "default"}
                size="large"
                shape="round"
              >
                {humanLabel}
              </Button>
            );
          })}
        </TimeBlocks>
        {allSlots.length > 0 && (
          <Select
            onChange={handleChange}
            value={requestInFirstSlots ? "" : requestedPickupTime}
            defaultValue=""
            size="large"
            style={{ width: "100%" }}
          >
            <Option key="default" value="">
              More times
            </Option>
            {allSlots.map(({ timeSlot, available }) => {
              const humanLabel = createHumanReadable(timeSlot, timezone);
              return (
                <Option key={timeSlot} value={timeSlot} disabled={!available}>
                  {humanLabel}
                </Option>
              );
            })}
          </Select>
        )}
      </>
    );
  };

  const Title: FC = () => {
    return (
      <StyledTitle>
        {shopLocation.lead_time ? (
          <div>
            Pickup Time - Current Prep Time:{" "}
            <span>{`${shopLocation.lead_time} minutes`}</span>
          </div>
        ) : (
          "Pickup Time"
        )}
      </StyledTitle>
    );
  };

  return (
    <StyledCard
      loading={loadingUpdate || availabilityQuery.loading}
      title={<Title />}
      actions={
        !showEditTop && !showEditOptions
          ? [editButton]
          : requestedPickupTime && showEditOptions
          ? [cancelButton]
          : []
      }
      extra={showEditTop ? editButton : null}
    >
      {!showEditOptions && requestedPickupTime ? (
        <Preview
          requestedPickupTime={requestedPickupTime}
          timezone={timezone}
        />
      ) : (
        <Content />
      )}
    </StyledCard>
  );
};

export const Preview: FC<{ requestedPickupTime: string; timezone: string }> = ({
  requestedPickupTime,
  timezone,
}) =>
  requestedPickupTime === ASAP ? (
    <div>ASAP</div>
  ) : (
    <div>
      {tzMoment(requestedPickupTime)
        .tz(timezone)
        .format(HUMAN_OUTPUT_FORMAT + " z")}
    </div>
  );

const TimeBlocks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;

  button {
    margin-bottom: 1rem;
  }
`;

const Message = styled.div`
  padding-bottom: 1rem;
`;

const StyledCard = styled(Card)`
  .ant-card-head-wrapper .ant-card-head-title {
    white-space: normal;
    text-overflow: initial;
  }
`;

const StyledTitle = styled.div`
  @media (max-width: 550px) {
    text-align: center;
    span {
      display: block;
    }
  }
`;

export default TimeScheduler;
